import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, HStack, Portal, Text, VStack, useColorModeValue } from '@chakra-ui/react'
import { IconType } from 'react-icons'

export interface ContextMenuItem {
  /** Optional — omit for icon-free menus. */
  icon?: IconType
  label: string
  onClick: () => void
  isDestructive?: boolean
}

interface ContextMenuProps {
  isOpen: boolean
  position: { x: number; y: number }
  onClose: () => void
  items: ContextMenuItem[]
}

const VIEWPORT_MARGIN = 8

function ContextMenu({ isOpen, position, onClose, items }: ContextMenuProps) {
  const [adjustedPos, setAdjustedPos] = useState<{ x: number; y: number } | null>(null)
  const menuNodeRef = useRef<HTMLDivElement | null>(null)

  const menuBg = useColorModeValue('white', 'gray.700')
  const menuBorder = useColorModeValue('gray.200', 'gray.600')
  const itemHoverBg = useColorModeValue('gray.100', 'gray.600')
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const destructiveHoverBg = useColorModeValue('red.50', 'red.900')
  const destructiveTextColor = useColorModeValue('red.600', 'red.300')
  const destructiveIconColor = useColorModeValue('#e53e3e', '#fc8181')

  // Clamp the menu inside the viewport once its size is known. Measured via a
  // callback ref because Chakra's Portal mounts its children a tick after the
  // first render, so a layout effect on open would fire before the node exists.
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuNodeRef.current = node
      if (!node) return

      const rect = node.getBoundingClientRect()
      setAdjustedPos({
        x: Math.max(VIEWPORT_MARGIN, Math.min(position.x, window.innerWidth - rect.width - VIEWPORT_MARGIN)),
        y: Math.max(VIEWPORT_MARGIN, Math.min(position.y, window.innerHeight - rect.height - VIEWPORT_MARGIN)),
      })
    },
    [position.x, position.y]
  )

  // Reset so a reopen doesn't paint one frame at the previous clamped spot
  useEffect(() => {
    if (!isOpen) {
      setAdjustedPos(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleDismiss = () => onClose()
    // Capture phase, so rows that stopPropagation() on their own click/contextmenu
    // handlers still dismiss the menu; events inside the menu itself are ignored.
    const handleOutsidePress = (event: Event) => {
      const node = menuNodeRef.current
      if (node && event.target instanceof Node && node.contains(event.target)) {
        return
      }
      onClose()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleOutsidePress, true)
    document.addEventListener('contextmenu', handleOutsidePress, true)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', handleDismiss)
    window.addEventListener('scroll', handleDismiss, true)

    return () => {
      document.removeEventListener('mousedown', handleOutsidePress, true)
      document.removeEventListener('contextmenu', handleOutsidePress, true)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleDismiss)
      window.removeEventListener('scroll', handleDismiss, true)
    }
  }, [isOpen, onClose])

  if (!isOpen) {
    return null
  }

  const renderPos = adjustedPos ?? position

  return (
    <Portal>
      <Box
        ref={measureRef}
        position="fixed"
        top={`${renderPos.y}px`}
        left={`${renderPos.x}px`}
        zIndex={1000}
        minW="150px"
        bg={menuBg}
        border="1px solid"
        borderColor={menuBorder}
        borderRadius="md"
        boxShadow="lg"
        py={1}
        visibility={adjustedPos ? 'visible' : 'hidden'}
      >
        <VStack spacing={0} align="stretch">
          {items.map((item) => (
            <Box
              key={item.label}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: item.isDestructive ? destructiveHoverBg : itemHoverBg }}
              onClick={() => {
                item.onClick()
                onClose()
              }}
            >
              <HStack spacing={2}>
                {item.icon && (
                  <item.icon
                    size={14}
                    color={item.isDestructive ? destructiveIconColor : undefined}
                  />
                )}
                <Text fontSize="sm" color={item.isDestructive ? destructiveTextColor : textColor}>
                  {item.label}
                </Text>
              </HStack>
            </Box>
          ))}
        </VStack>
      </Box>
    </Portal>
  )
}

export default ContextMenu
